import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './input.scss';
import Multiselect from 'multiselect-react-dropdown';
import PhoneNumberInput from './PhoneNumberInput';
import { icons } from '../../../assets';
import { useClickOutside } from '../../../hooks/useClickOutside';

interface IInput {
  pattern?: string;
  type?: string;
  label?: string;
  ctaText?: string;
  ctaRoute?: string;
  obscureText?: boolean;
  onChange?: (name: string, value: string) => void;
  hasError?: boolean;
  errorText?: string;
  readonly?: boolean;
  placeholder?: string;
  name: string;
  value: string;
  required?: boolean;
  onBlur?: () => void;
  defaultValue?: string;
  handleCTAClick?: () => void;
  info?: string;
  infoOpen?: boolean;
  accept?: string;
  min?: number;
  maxLength?: number;
  id?: string;
  disabled?: boolean;
  wrapperClass?: string;
  inputClass?: string;
}


function Input(props: IInput) {
  const [inputType, setType] = useState(props.type || 'text');
  const isPassword = inputType === 'password';
  const handleViewPassword = (event) => {
    event.preventDefault();
    if (isPassword) {
      setType('text')
    } else {
      setType('password');
    }
  }

  return (
    <div className={`input-wrapper ${props.wrapperClass}`}>
      <InputLabel props={props} />
      {props.type === 'password' && (
        <button onClick={handleViewPassword} className="open-password">
          <span>{isPassword ? 'Show' : 'Hide'}</span>
        </button>
      )
      }
      {
        props.type && props.type === 'file' ? (
          <input
            type="file"
            required={props.required}
            onChange={({ target }) => {
              //@ts-ignore
              props.onChange && props.onChange(props.name, target.files[0])
            }}
            onBlur={() => {
              props.onBlur && props.onBlur()
            }}
            accept={props.accept}
            style={{
              backgroundColor: 'transparent',
            }}
            className={`${props.inputClass}`}
          />
        ) : props.type === "tel" ?
          (
            <PhoneNumberInput
              height={"12"}
              value={props.value}
              required={props.required}
              handleChange={(e) => {
                props.onChange && props.onChange(props.name, e.target.value);
              }}
              onBlur={() => {
                props.onBlur && props.onBlur()
              }}
              min={props.min}
              name={props.name}
              pattern={props.pattern || '.*?'}
              readOnly={props.readonly}
              defaultValue={props.defaultValue}
            />
          )
          : props.maxLength ? (
            <textarea
              defaultValue={props.defaultValue}
              placeholder={props.placeholder}
              name={props.name}
              value={props.value}
              required={props.required}
              onChange={({ target }) => {
                props.onChange && props.onChange(props.name, target.value);
              }}
              onBlur={() => {
                props.onBlur && props.onBlur()
              }}
              className='!py-2 !resize-none !h-20 !mt-0'
              rows={10}
              maxLength={props.maxLength}
              disabled={props.disabled}
            >

            </textarea>
          ) : (
            <input
              type={inputType}
              readOnly={props.readonly}
              defaultValue={props.defaultValue}
              placeholder={props.placeholder}
              name={props.name}
              value={props.value}
              required={props.required}
              pattern={props.pattern || '.*?'}
              onChange={({ target }) => {
                const val = inputType === 'checkbox' ? target.checked : target.value;
                // @ts-ignore
                props.onChange && props.onChange(props.name, val)
              }}
              onBlur={() => {
                props.onBlur && props.onBlur()
              }}
              min={props.min}
              id={props.id}
              disabled={props.disabled}
              className={`disabled:text-gray-400 disabled:cursor-not-allowed ${props.inputClass}`}
            />
          )
      }
      {props.hasError && <p>{props.errorText}</p>}
      {props.maxLength ? <span className='self-end'>{props.value?.length || 0}/{props.maxLength}</span> : ''}
    </div>
  );
}

export default Input;

export const Select = ({
  data,
  value,
  ...props
}: {
  data: Array<string>;
  type?: string;
  value?: string;
  label?: string;
  ctaText?: string;
  ctaRoute?: string;
  obscureText?: boolean;
  onChange?: (name: string, value: string) => void;
  hasError?: boolean;
  errorText?: string;
  readonly?: boolean;
  placeholder?: string;
  name: string;
  required?: boolean;
  onBlur?: () => void;
  defaultValue?: string;
  className?: string;
  selectClassName?: string;
  info?: string;
  infoOpen?: boolean;
}) => {
  return (
    <div className={`input-wrapper ${props.className}`}>
      <InputLabel props={props} />
      <select
        className={`${props.selectClassName}`}
        name={props.name}
        value={value}
        required={props.required}
        onChange={({ target }) => {
          props.onChange && props.onChange(props.name, target.value)
        }}
        onBlur={() => {
          props.onBlur && props.onBlur()
        }}
      >
        <option disabled></option>
        {
          data && data.map((item, index) => <option value={item} key={`${item}-${index}}`}>{item}</option>)
        }
      </select>
      {props.hasError && <p>{props.errorText}</p>}
    </div>
  )
}

export const AdvancedSelect = ({
  data,
  value,
  ...props
}: {
  data: Array<{
    label: string,
    value: string | number
  }>;
  type?: string;
  value?: string;
  label?: string;
  ctaText?: string;
  ctaRoute?: string;
  obscureText?: boolean;
  onChange?: (name: string, value: string) => void;
  hasError?: boolean;
  errorText?: string;
  readonly?: boolean;
  placeholder?: string;
  name: string;
  required?: boolean;
  onBlur?: () => void;
  defaultValue?: string;
  className?: string;
  selectClassName?: string;
  info?: string;
}) => {
  return (
    <div className={`input-wrapper ${props.className}`}>
      <InputLabel props={props} />
      <select
        className={`${props.selectClassName}`}
        name={props.name}
        value={value}
        required={props.required}
        onChange={({ target }) => {
          props.onChange && props.onChange(props.name, target.value)
        }}
        onBlur={() => {
          props.onBlur && props.onBlur()
        }}
      >
        <option disabled>{props.placeholder}</option>
        {
          data && data.map((item, index) => <option value={item?.value} key={`${item?.value}-${index}}`}>{item?.label}</option>)
        }
      </select>
      {props.hasError && <p>{props.errorText}</p>}
    </div>
  )
}

export const CustomMultiSelect = ({
  data,
  value,
  ...props
}: {
  data: Array<{
    label: string,
    value: string | number
  }>;
  type?: string;
  value?: string;
  label?: string;
  ctaText?: string;
  ctaRoute?: string;
  obscureText?: boolean;
  onChange?: (name: string, value: string) => void;
  hasError?: boolean;
  errorText?: string;
  readonly?: boolean;
  placeholder?: string;
  name: string;
  required?: boolean;
  onBlur?: () => void;
  defaultValue?: string;
  className?: string;
  selectClassName?: string;
  info?: string;
}) => {
  return (
    <div className={`input-wrapper ${props.className}`}>
      <InputLabel props={props} />
      <div className="w-[100%] [&>*]:w-[100%] p-0 mt-[0px] h-[45px] mb-14">
        <Multiselect
          selectedValues={value}
          options={data} // Options to display in the dropdown
          onSelect={(selectedList) => {
            props.onChange && props.onChange(props.name, selectedList.map(i => i.value))
          }} // Function will trigger on select event
          onRemove={(selectedList) => {
            props.onChange && props.onChange(props.name, selectedList.map(i => i.value))
          }} // Function will trigger on remove event
          displayValue="label" // Property name to display in the dropdown options
          placeholder={props.placeholder}
          avoidHighlightFirstOption={true}
          showArrow={true}
          style={{
            chips: { // To change css chips(Selected options)
              background: "#2E8184",
              color: "white",
              margin: "5px"
            },
            searchBox: {
              padding: "0",
              maxHeight: "80px",
              overflow: "auto",
            },
            inputField: {
              height: "45px",
              margin: "0px"
            }
          }}
        />
      </div>
    </div>
  )
}


export const InputLabel = ({ props }) => {
  const [openInfo, setOpenInfo] = useState(false);
  const infoRef = useRef(null)
  useClickOutside(infoRef, () => setOpenInfo(false));


  useEffect(() => {
    if (props.infoOpen) {
      setOpenInfo(true)
    }
  }, [props.infoOpen])

  return (
    <div className='label-wrapper'>
      {props.label && <label htmlFor={props.name}>{props.label} {props.required && (<sup className='text-red-500 font-bold'>*</sup>)}</label>}

      {
        props.handleCTAClick ? (
          <span onClick={props.handleCTAClick} className='underline underline-offset-0 cursor-pointer'>{props.ctaText}</span>
        ) : <Link to={`/${props.ctaRoute}`}><span className='underline'>{props.ctaText}</span></Link>
      }
      {props.info && <img className='open-info' src={icons.help} alt="" onMouseLeave={() => setOpenInfo(!openInfo)} onMouseEnter={() => setOpenInfo(!openInfo)} />}
      
      {
        (props.info && openInfo) &&
        (<div className="info" ref={infoRef}>
          <div>{props.info}</div>
        </div>)
      }
    </div>
  )
}
